import React, { Fragment, useState, useEffect } from 'react';
import TableBuilder, { ApiRequest, ButtonBuilder, ModalButtonBuilder, ToastBuilder, HoverOver, ActionGroupBuilder } from '../../../../../../../GlobalComponents/Builders';
import { Row, Col, CardHeader, CardBody, Card } from 'reactstrap';
import { H4 } from '../../../../../../../AbstractElements';

const STATUS_COLORS = {
    Approved: '#198754',
    Pending: '#ffc107',
    Missing: '#dc3545',
};

import ResourceLabel from './ResourceLabel';

const DefaultKeyMappings = () => {
    const [mappings, setMappings] = useState([]);
    const [isProcessing, setIsProcessing] = useState(true);
    const [processingRows, setProcessingRows] = useState({});

    useEffect(() => { loadMappings(); }, []);

    const loadMappings = () => {
        ApiRequest('GET', '/dmt/defaults/keyMappings', { setIsProcessing }).then((results) => {
            if (results.data) setMappings(results.data);
        });
    };

    const rowKey = (row) => `${row.source_resource_key_id}-${row.destination_resource_key_id}`;

    const handleApprove = (row) => {
        const key = rowKey(row);
        setProcessingRows(prev => ({ ...prev, [key]: true }));
        ApiRequest('PATCH', '/dmt/defaults/keyMappings', {
            body: {
                sourceResourceId: row.source_resource_id,
                sourceKey: row.source_key,
                destinationResourceId: row.destination_resource_id,
                destinationKey: row.destination_key,
                isApproved: row.key_mapping_approved ? 0 : 1,
                action: 'approve',
            }
        }).then((results) => {
            setProcessingRows(prev => ({ ...prev, [key]: false }));
            if (results?.data?.success) {
                ToastBuilder('success', `Mapping ${row.key_mapping_approved ? 'unapproved' : 'approved'}`);
                loadMappings();
            }
        });
    };

    const handleDelete = (row) => {
        const key = rowKey(row);
        setProcessingRows(prev => ({ ...prev, [key]: true }));
        ApiRequest('PATCH', '/dmt/defaults/keyMappings', {
            body: {
                sourceResourceId: row.source_resource_id,
                sourceKey: row.source_key,
                destinationResourceId: row.destination_resource_id,
                destinationKey: row.destination_key,
                action: 'delete',
            }
        }).then((results) => {
            setProcessingRows(prev => ({ ...prev, [key]: false }));
            if (results?.data?.success) {
                ToastBuilder('success', 'Mapping deleted');
                loadMappings();
            }
        });
    };

    return (
        <Fragment>
            <Row>
                <Col lg={12}>
                    <Card>
                        <CardHeader className='d-flex justify-content-between align-items-center' style={{ paddingBottom: 0 }}>
                            <div style={{ maxWidth: '80%' }}>
                                <H4 attrH4={{ className: 'card-title mb-0' }}>Default Key Mappings</H4>
                                <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: 2, marginTop: 4 }}>Standard key-to-key mapping templates, auto-generated per resource from approved jobs.</p>
                                <p className='text-muted mb-0' style={{ fontSize: '12px' }}>
                                    Auto-generated key-to-key mapping defaults derived from approved job key mappings (codes 4/5).
                                    These will be used to pre-populate key mappings when new jobs are created for the same service pair.
                                    Grouped by source/destination key across all jobs. Table: <code>dmt_default_key_mapping</code>
                                </p>
                            </div>
                        </CardHeader>
                        <CardBody style={{ paddingTop: 0 }}>
                            <TableBuilder
                                subHeader={true}
                                columnsToSearch={['source_key', 'destination_key', 'source', 'destination', 'code_name', 'status', 'ref_source_resource_name', 'ref_dest_resource_name']}
                                data={mappings}
                                processing={isProcessing}
                                pagination={true}
                                sortField={null}
                                columns={[
                                    {
                                        name: 'Status',
                                        selector: (row) => {
                                            const approved = row.key_mapping_approved;
                                            const label = approved === 1 ? 'Approved' : approved === 0 ? 'Pending' : 'No Default';
                                            const color = approved === 1 ? STATUS_COLORS.Approved : approved === 0 ? STATUS_COLORS.Pending : STATUS_COLORS.Missing;
                                            return <span className='badge' style={{ backgroundColor: color }}>{label}</span>;
                                        },
                                        sortable: true,
                                        center: true,
                                        maxWidth: '8%',
                                    },
                                    {
                                        name: 'Source',
                                        selector: (row) => <ResourceLabel name={row.source_resource_type} serviceCode={row.source_service_code} />,
                                        sortable: true,
                                        sortFunction: (a, b) => (a.source_resource_type || '').localeCompare(b.source_resource_type || ''),
                                    },
                                    {
                                        name: 'Source Key',
                                        selector: (row) => row.source_key || '-',
                                        sortable: true,
                                    },
                                    {
                                        name: 'Dest',
                                        selector: (row) => <ResourceLabel name={row.destination_resource_type} serviceCode={row.destination_service_code} />,
                                        sortable: true,
                                        sortFunction: (a, b) => (a.destination_resource_type || '').localeCompare(b.destination_resource_type || ''),
                                    },
                                    {
                                        name: 'Dest Key',
                                        selector: (row) => row.destination_key || '-',
                                        sortable: true,
                                    },
                                    {
                                        name: 'Code',
                                        selector: (row) => row.code_name || row.code || '-',
                                        sortable: true,
                                        center: true,
                                        maxWidth: '10%',
                                    },
                                    {
                                        name: 'Ref',
                                        selector: (row) => row.ref_source_resource_name
                                            ? <HoverOver content={`${row.ref_source_resource_name} → ${row.ref_dest_resource_name || '?'}`} text={row.ref_source_resource_name} />
                                            : '-',
                                        sortable: true,
                                        maxWidth: '12%',
                                    },
                                    {
                                        name: 'Jobs',
                                        selector: (row) => row.count || 0,
                                        sortable: true,
                                        center: true,
                                        maxWidth: '7%',
                                    },
                                    {
                                        name: 'Actions',
                                        center: true,
                                        maxWidth: '11%',
                                        selector: (row) => (
                                            <ActionGroupBuilder
                                                isApproved={row.key_mapping_approved}
                                                disabled={processingRows[rowKey(row)]}
                                                onApprove={() => handleApprove(row)}
                                                onDelete={() => handleDelete(row)}
                                                showBurger={row.key_mapping_approved !== undefined && row.key_mapping_approved !== null}
                                                modalTitle='Default Key Mapping Details'
                                                modalBody={
                                                    <table className='table table-borderless table-sm mb-0'>
                                                        <tbody>
                                                            <tr><td className='text-muted' style={{width:'40%'}}>Source</td><td>{row.source_resource_type} ({row.source_service_code})</td></tr>
                                                            <tr><td className='text-muted'>Source Key</td><td>{row.source_key}</td></tr>
                                                            <tr><td className='text-muted'>Destination</td><td>{row.destination_resource_type} ({row.destination_service_code})</td></tr>
                                                            <tr><td className='text-muted'>Destination Key</td><td>{row.destination_key}</td></tr>
                                                            <tr><td className='text-muted'>Code</td><td>{row.code_name || row.code || '-'}</td></tr>
                                                            <tr><td className='text-muted'>Ref</td><td>{row.ref_source_resource_name ? `${row.ref_source_resource_name} → ${row.ref_dest_resource_name || '?'}` : '-'}</td></tr>
                                                            <tr><td className='text-muted'>Notes</td><td>{row.key_mapping_notes || '-'}</td></tr>
                                                            <tr><td className='text-muted'>Jobs</td><td>{row.job_ids || '-'}</td></tr>
                                                        </tbody>
                                                    </table>
                                                }
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default DefaultKeyMappings;
